import { useState, useCallback, useEffect } from 'react';
import { Role } from 'types/role';
import { useRoleService } from 'services/role';
import { useActiveCompanyStore } from 'stores/activeCompanyStore';

interface UseRoleDataProps {
  autoFetch?: boolean;
}

export function useRoleData({ autoFetch = true }: UseRoleDataProps = {}) {
  const roleService = useRoleService();
  const activeCompanyId = useActiveCompanyStore((s) => s.activeCompanyId);
  const isCompanyInitialized = useActiveCompanyStore((s) => s.isInitialized);
  const [roles, setRoles] = useState<Role[]>([]);
  const [loading, setLoading] = useState(false);
  const [reloading, setReloading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchRoles = useCallback(
    async (isReload = false) => {
      if (!isCompanyInitialized || !activeCompanyId) {
        return;
      }

      if (isReload) {
        setReloading(true);
      } else {
        setLoading(true);
      }
      setError(null);
      try {
        const data = await roleService.getAllRoles();
        setRoles(data);
      } catch (err) {
        console.error('Failed to fetch roles:', err);
        setError('Failed to load roles. Please try again.');
      } finally {
        setLoading(false);
        setReloading(false);
      }
    },
    [roleService, isCompanyInitialized, activeCompanyId],
  );

  const reload = useCallback(() => {
    void fetchRoles(true);
  }, [fetchRoles]);

  useEffect(() => {
    if (autoFetch && isCompanyInitialized && activeCompanyId) {
      void fetchRoles();
    }
  }, [autoFetch, fetchRoles, isCompanyInitialized, activeCompanyId]);

  const addRoleToState = useCallback((role: Role) => {
    setRoles((prev) => [role, ...prev]);
  }, []);

  const updateRoleInState = useCallback((updatedRole: Role) => {
    setRoles((prev) =>
      prev.map((r) => (r.id === updatedRole.id ? updatedRole : r)),
    );
  }, []);

  const removeRoleFromState = useCallback((id: string) => {
    setRoles((prev) => prev.filter((r) => r.id !== id));
  }, []);

  return {
    roles,
    loading,
    reloading,
    error,
    reload,
    addRole: addRoleToState,
    updateRole: updateRoleInState,
    removeRole: removeRoleFromState,
  };
}
